import { createFileRoute } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { WORKFORCE_LIST, breadcrumbs } from "@/lib/marketing/ppl-schema";
import { MLink } from "@/lib/marketing/m-link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Reveal } from "@/components/marketing/reveal";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { MarketingCta } from "@/components/marketing/site/marketing-cta";
import { SectionHeading } from "@/components/marketing/site/section-heading";
import { AriaDemoSection } from "@/components/marketing/site/live-demos";
import { SarahDemoSection } from "@/components/marketing/site/sarah-demo-section";
import { ProofSection } from "@/components/marketing/site/proof-section";
import { NancyRunSection } from "@/components/marketing/proof/nancy-run";
import { DESK_EMPLOYEES, EMPLOYEES, TRACES } from "@alm/domain/marketing";
import { CAPABILITIES } from "@alm/domain/marketing";
import { ArrowRight } from "@/lib/public-icons";
import { RosterGrid } from "@/components/marketing/site/roster-grid";
import { WorkforceHero } from "@/components/marketing/site/workforce-hero";
import { WorkTrace } from "@/components/marketing/site/work-trace";
import { AriaCard } from "@/routes/v2/-components/aria-card";
import { ARIA_CAPABILITIES } from "@/routes/v2/-components/workforce.index-shared";
import { WorkforceAnatomy } from "@/routes/v2/-components/workforce-anatomy";
import { WorkforceDesks } from "@/routes/v2/-components/workforce-desks";

/** The roster and the record it runs, read from workforce.ts and record.ts and never restated, so
 * site and product do not drift. Light surface: the dark skin's bare `a`, `button` and `.label`
 * rules would repaint the nav and footer. */

/* Aria's entries carry no `operatedBy` (record.ts leaves it to the running desk, and she runs
 * none). Selecting by slug keeps her out of the desk and platform lists. */
const ARIA_RECORD = CAPABILITIES.find((c) => c.slug === "aria");

/* Eight desk owners go in the grid and the superagent gets its own treatment. Reading
 * DESK_EMPLOYEES keeps a new name from appearing as a ninth desk. */
const SUPERAGENTS = EMPLOYEES.filter((e) => e.kind === "superagent");

function PeopleWorkforce() {
  return (
    <MarketingShell>
      <WorkforceHero />

      {/* The roster. The superagent leads it: Aria is the interface to the
          record every desk works in, and the one member a visitor meets first,
          so the card that says so sits above the eight rather than after them. */}
      <section>
        <div className="mx-auto max-w-6xl px-4 pt-6 pb-20 md:pt-10 md:pb-28">
          {SUPERAGENTS.length > 0 && (
            <div data-testid="roster-superagent">
              <Reveal>
                <h2 className="text-sm font-medium text-muted-foreground">
                  The superagent: how a person works all of them
                </h2>
              </Reveal>
              <div className="mt-5 space-y-4">
                {SUPERAGENTS.map((superagent) => (
                  <Reveal key={superagent.slug} delay={60}>
                    <AriaCard employee={superagent} />
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          <Reveal>
            <p className="mt-14 text-sm font-medium text-muted-foreground">The desks</p>
          </Reveal>
          {/* Only the desks go to the shared RosterGrid; Aria has her card above, and the grid
              would add its own superagent block. */}
          <div className="people-light on-field mt-8">
            <RosterGrid employees={[...DESK_EMPLOYEES]} />
          </div>

          <Reveal>
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
              <span className="italic">Hire one. Or the whole company.</span>
              {/* The source site says "Eight roles at launch". A launch date
                  reads as risk to a buyer deciding whether anyone runs this yet,
                  and the workforce is already running at two communities. */}
              <span>These desks today, hundreds over time</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Live demos right after the roster: Aria first (the superagent card's claim, tested), then
          Sarah. Both lazy-load and remove themselves if the demo tenant is not seeded
          (live-demos.tsx). */}
      <AriaDemoSection
        id="aria-demo"
        kicker="Live, Aria"
        title="The superagent, on the record we made up"
        lede="You have just read what Aria is. This is Aria. The community below is invented from end to end, and she works it the way she works a real one, reading every module and writing back to it. Ask in plain English and the answer comes back as an interface built for the question, not a paragraph about it."
      />

      <SarahDemoSection
        id="talk-to-sarah"
        kicker="Live, Sarah"
        title="Sarah is on the desk. Ring her."
        lede="The receptionist above, answering out loud, over the same telephone-quality audio a caller on a real phone hears. Ask for one of her colleagues by name and listen to what she does when they cannot pick up, the handling is the job, and it is the only part a roster card cannot show you."
      />

      {/* The third of the set. Nancy's cannot be live — see the note on the section — but it
          belongs beside the other two, not on a page of its own where nobody scrolls to it. It sat
          here on the home page too, between Sarah and the proof, and it keeps that place. */}
      <NancyRunSection />

      {/* Three charts from the live communities' records (Sarah's call volume, Sarah's after-hours
          coverage, Nancy's food cost against budget), each linking to the study that explains the
          measurement. */}
      <ProofSection />

      {/* The superagent */}
      <section className="border-t border-border/60 bg-card/40" data-testid="aria-superagent">
        <div className="mx-auto max-w-6xl px-4 py-20 md:py-28">
          <Reveal>
            <SectionHeading
              eyebrow="Aria, the superagent"
              title="Your agent inside the operating system."
              subtitle={ARIA_RECORD?.summary}
            />
          </Reveal>
          {ARIA_RECORD?.detail && (
            <Reveal delay={60}>
              <p className="mx-auto mt-8 max-w-3xl text-muted-foreground">{ARIA_RECORD.detail}</p>
            </Reveal>
          )}
          {/* Cards, since five one-liners in a list read as a footnote to the eight above. */}
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {ARIA_CAPABILITIES.map((capability, i) => (
              <Reveal key={capability.slug} delay={(i % 2) * 70}>
                <Card className="h-full p-6" data-testid={`card-${capability.slug}`}>
                  <h3 className="text-base font-semibold tracking-tight">{capability.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{capability.summary}</p>
                </Card>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Button className="font-ui" data-testid="link-roster-aria" asChild>
                <MLink href="/workforce/aria">
                  Meet Aria
                  <ArrowRight className="ml-2 size-4" />
                </MLink>
              </Button>
              <p className="text-sm text-muted-foreground">
                Not a ninth desk: the assistant every desk, and every person, works through.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <WorkforceAnatomy />

      <WorkforceDesks />

      {/* Every role, at work */}
      <section>
        <div className="mx-auto max-w-6xl px-4 py-20 md:py-28">
          <Reveal>
            <SectionHeading
              eyebrow="The work"
              title="Every role, at work."
              subtitle="One real instruction per role: and the outcome it delivered. The moments that belong to people stay with people; in a live demo we'll show you every step in between."
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {TRACES.map((trace, i) => (
              <Reveal key={trace.who} delay={(i % 4) * 70}>
                <WorkTrace trace={trace} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MarketingCta
        title="Hire one. Or the whole company."
        subtitle="New roles arrive in weeks, not product cycles."
        primaryLabel="Get a demo"
        secondaryLabel="Run a med pass"
        secondaryHref="/experience"
      />
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/workforce/")({
  component: PeopleWorkforce,
  head: () => ({
    ...marketingHead({
      title:
        "The AI Workforce: every desk, one superagent, one system of record | PEOPLE AI Workforce",
      description:
        "Amy, Sarah, Olivia, David, Nancy, James, Michael and Sophia, the AI employees that run your community's administrative company on one system of record.",
      path: "/v2/workforce",
      imageAlt: "PEOPLE AI Workforce. The operating system for assisted living communities",
      schema: [WORKFORCE_LIST, breadcrumbs({ name: "AI Workforce", path: "/workforce" })],
    }),
  }),
});
